<template>
  <div>
    <div class="card-header">
      <h2 class="card-title">All Documents</h2>
    </div>

    <div class="card-header">
      <div class="d-flex w-100">
        <div class="flex-grow-1">
          <select class="form-control" @change="setType($event.target.value)">
            <option value="">All Categories</option>
            <option
              :key="index"
              :value="type.name"
              v-for="(type, index) in categoryOptions"
            >
              {{ type.label }}
            </option>
          </select>
        </div>
        <div class="flex-grow-0 pl-3">
          <form-button
            :indicator="selectedDownloading"
            v-on:clicked="downloadSelectedDocuments"
          >
            Download
          </form-button>
        </div>
      </div>
    </div>

    <table class="table table-bordered table-striped">
      <thead>
        <tr>
          <th>
            <input
              type="checkbox"
              v-model="selectAll"
              @change="toggleSelectAll"
            />
            Select All
          </th>
          <th>Document Type</th>
          <th>Document Description</th>
          <th>Uploaded</th>
          <th>&nbsp;</th>
        </tr>
      </thead>
      <tbody>
        <tr v-bind:key="index" v-for="(document, index) in documents">
          <td>
            <input
              type="checkbox"
              v-model="selectedDocuments"
              :value="document"
            />
          </td>
          <td>{{ getDocumentLabel(document) }}</td>
          <td>{{ getDocumentDescription(document) }}</td>
          <td>{{ document.created_at | dateFormat }}</td>
          <td>
            <form-button
              :indicator="downloading === document.identifier"
              @clicked="viewDocument(document)"
            >
              View
            </form-button>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
<script>
import FormButton from '@/components/Generic/Form/FormButton';

export default {
  components: { FormButton },
  props: {
    incident: {
      type: String,
      required: true
    },
    recovery: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      documents: [],
      filters: {
        type_id: ''
      },
      selectedDownloading: false,
      downloading: null,
      selectAll: false,
      selectedDocuments: [],
      selectedCategory: null,
      categoryOptions: [
        { name: 'engineers_fee', label: 'Engineers fee' },
        { name: 'inherited_costs', label: 'Inherited costs' },
        { name: 'loss_of_use_dates', label: 'Loss of use dates' },
        { name: 'loss_of_use_invoice', label: 'Loss of use invoice' },
        { name: 'mitigation_statement', label: 'Mitigation statement' },
        { name: 'other_losses_for_client', label: 'Other losses' },
        { name: 'r_and_s_invoice', label: 'R&S invoice' },
        { name: 'repair_authority', label: 'Repair authority' },
        { name: 'repair_estimate', label: 'Repair estimate' },
        { name: 'repair_invoice', label: 'Repair invoice' },
        { name: 'request_for_payment', label: 'Payment request letter' },
        {
          name: 'signed_credit_hire_agreement',
          label: 'Signed Credit hire agreement with T’s and C’s'
        },
        {
          name: 'signed_r_and_s_agreement',
          label: 'Signed R&S agreement, including T’s and C’s'
        }
      ],
      invoiceRateGroup: undefined
    };
  },

  computed: {
    types() {
      return this.$store.getters['DocumentTypes/all'] || [];
    },
    rateGroups() {
      return this.$store.getters['RateGroups/all'];
    }
  },

  mounted() {
    this.$store.commit('DocumentTypes/retrieve');

    this.getDocuments();
  },

  filters: {
    dateFormat(value) {
      // Check if the value has already been formatted, if it has just return it
      if (/^\d{2}\/\d{2}\/\d{4}\s\d{2}:\d{2}/.test(value)) {
        return value;
      }

      if (value) {
        return window.DateManager.format(value, 'DD/MM/YYYY HH:mm');
      }

      return null;
    }
  },

  methods: {
    getDocuments() {
      window.Ajax.get(
        `/incidents/${this.incident}/documents?filter[type_id]=${this.filters
          .type_id || this.defaultTypes()}`
      )
        .then(response => {
          this.documents = response.data;

          // For certain categories we want to add the generated recovery invoices
          // to the documents list. When we set the filter type we check if we need
          // any of the invoices to be displayed, if we do we set the relevant rate
          // group to the invoiceRateGroup property. Here, we then check if that
          // property has been set, and if it has we get all of the relevant invoices.
          //
          // If a type_id filter has not been set then we add all the invoices to the
          // documents list.
          const invoices = this.recovery.invoices
            .filter(invoice => {
              if (!this.filters.type_id) {
                return true;
              }

              return this.invoiceRateGroup
                ? invoice.rate_groups.includes(this.invoiceRateGroup.id)
                : false;
            })
            .map(invoice => ({
              ...invoice,
              doc_type: 'invoice'
            }));

          // We want the invoices to be at the top of the list of documents. Here,
          // we are updating the documents array and using the spread operator to
          // prepend the invoices to the top of the list.
          this.documents = [...invoices, ...this.documents];
        })
        .catch(error => {});
    },

    defaultTypes() {
      const mappedTypes = this.categoryOptions
        .map(category => this.getTypeNames(category.name))
        .flat();

      const matchedTypes = this.types.filter(t => mappedTypes.includes(t.name));

      return matchedTypes.map(t => t.id).join(',');
    },

    getDocumentLabel(document) {
      return document.doc_type === 'invoice'
        ? 'Invoice'
        : document.type?.label || 'Not Provided';
    },

    getDocumentDescription(document) {
      return document.doc_type === 'invoice'
        ? `${document.formatted_invoice_number} - ${this.rateGroupsLabel(
            document
          )}`
        : document.description;
    },

    rateGroupsLabel(invoice) {
      return this.rateGroups
        .filter(group => invoice.rate_groups.includes(group.id))
        .map(group => group.label)
        .join(', ');
    },

    toggleSelectAll() {
      if (this.selectAll) {
        this.selectedDocuments = this.documents.slice();
      } else {
        this.selectedDocuments = [];
      }
    },

    downloadSelectedDocuments() {
      this.selectedDownloading = true;

      const downloadPromises = this.selectedDocuments.map(document =>
        this.downloadDocument(document)
      );

      Promise.all(downloadPromises)
        .then(() => {
          this.selectedDownloading = false;
          window.toastify.info(`Success, documents downloaded successfully`);
        })
        .catch(error => {
          this.selectedDownloading = false;
        });
    },

    downloadDocument(document) {
      let url = document.url;

      if (document.doc_type === 'invoice') {
        url = `debt-recovery/${this.incident}/invoices/${document.identifier}/download`;
      }

      return new Promise((resolve, reject) => {
        window.Ajax.download(url)
          .then(() => {
            resolve();
          })
          .catch(error => {
            reject(error);

            window.toastify.error(
              `Sorry, there was a problem downloading ${this.getDocumentLabel(
                document
              )}`
            );
          });
      });
    },

    viewDocument(document) {
      this.downloading = document.identifier;

      let url = document.url;
      if (document.doc_type === 'invoice') {
        url = `debt-recovery/${this.incident}/invoices/${document.identifier}/download`;
      }

      window.Ajax.download(url)
        .then(() => {
          this.downloading = null;
        })
        .catch(() => {
          this.downloading = null;

          window.toastify.error(
            'Error!',
            `An error occurred while downloading the document`
          );
        });
    },

    setType(type) {
      // For some document categories we also want to be able to select
      // recovery invoices. Here, we are setting the relevant rate group
      // for the document category.
      this.setInvoiceRateGroup(type);

      if (type) {
        // For some document types we filter against multiple document categories
        // (for example the inherited costs type can include any inherited storage
        // invoices, recovery invoices etc.).
        //
        // Here, we get all of the relevant document category names for our selected
        // document type. This allows us to get the relevant IDs for our filter.
        const mappedTypes = this.getTypeNames(type);

        const matchedTypes = this.types.filter(t =>
          mappedTypes.includes(t.name)
        );

        this.filters.type_id = matchedTypes.map(t => t.id).join(',');
      } else {
        // If the type variable is empty then the user has selected the "All Categories"
        // type, so we reset the type_id.
        this.filters.type_id = '';
      }

      this.getDocuments();
    },

    getTypeNames(type) {
      // Create a map of document types to the relevant document category
      // name. You only need to do this if your document type key differs
      // from the relevant document category name.
      //
      // The value of the map should always be an array, this allows us to
      // treat single and multi-category types in the same way.
      const typeMappings = {
        signed_credit_hire_agreement: ['credit_hire_agreement'],
        signed_r_and_s_agreement: ['storage_and_recovery'],
        inherited_costs: [
          'storage_and_recovery_invoice',
          'repair_invoice',
          'paid_invoices',
          'repair_estimate'
        ]
      };

      let mappedType = typeMappings[type];

      // If the type is not defined in the map then we convert it to an
      // array, this allows us to filter the document categories in the
      // same way regardless of if there is one category or multiple.
      if (!mappedType) {
        mappedType = [type];
      }

      return mappedType;
    },

    /**
     * For some document types we also want to display recovery invoices
     * in the documents list. Here, we set the relevant recovery rate group
     * for the invoices we want to display.
     *
     * For example for the r_and_s_invoice document type we also want to
     * display recovery invoices. We set the invoiceRateGroup property to
     * the relevant rate group object so that we can filter the invoices
     * and add them to the documents later.
     *
     * If the document type does not require invoices we reset the
     * invoiceRateGroup property to undefined, this tells us to ignore
     * invoices when we fetch the documents.
     *
     * @param type
     */
    setInvoiceRateGroup(type) {
      switch (type) {
        case 'r_and_s_invoice':
          this.invoiceRateGroup = this.rateGroups.find(
            group => group.name === 'recovery'
          );
          break;

        case 'repair_invoice':
          this.invoiceRateGroup = this.rateGroups.find(
            group => group.name === 'repair'
          );
          break;

        default:
          this.invoiceRateGroup = undefined;
          break;
      }
    }
  }
};
</script>
